import React from "react";

// A bare http(s) URL in plain text: everything up to whitespace, an angle
// bracket or a double quote. Closing brackets and sentence punctuation are
// allowed INSIDE the run and trimmed off the end below, so a Wikipedia-style
// `…/Fund_(finance)` keeps its parenthesis while `(https://ex.com/x)` and
// `https://ex.com/a.` give theirs back to the prose.
const URL_RE = /https?:\/\/[^\s<>"]+/gi;
const TRAILING_PUNCTUATION_RE = /[.,;:!?'"]+$/;
const CLOSERS: Record<string, string> = { ")": "(", "]": "[", "}": "{" };

/** The URL with unbalanced closing brackets and sentence punctuation trimmed off its end. */
function trimUrl(run: string): string {
  let url = run;
  for (;;) {
    const before = url;
    url = url.replace(TRAILING_PUNCTUATION_RE, "");
    const last = url.at(-1) ?? "";
    const opener = CLOSERS[last];
    if (opener !== undefined) {
      const opens = url.split(opener).length - 1;
      const closes = url.split(last).length - 1;
      if (closes > opens) {
        url = url.slice(0, -1);
      }
    }
    if (url === before) {
      return url;
    }
  }
}

export type FoundLink = { start: number; end: number; url: string };

/**
 * Every bare http(s) URL in `text`, with its span in the text. The span ends
 * where the link does, so trailing punctuation the trim gave back stays in
 * the surrounding text. A scheme with nothing after it is not a link.
 */
export function findLinks(text: string): FoundLink[] {
  const links: FoundLink[] = [];
  for (const match of text.matchAll(URL_RE)) {
    const start = match.index ?? 0;
    const url = trimUrl(match[0]);
    if (!/^https?:\/\/[^/]/i.test(url)) {
      continue;
    }
    links.push({ start, end: start + url.length, url });
  }
  return links;
}

/**
 * Split plain text into strings and `<a>` elements for every bare http(s)
 * URL in it (#12201). For text the app deliberately does NOT run through
 * markdown — a user's own chat message — so a pasted link is still a link,
 * opening in a new tab, with the surrounding prose untouched.
 */
export function linkifyText(
  text: string,
  keyPrefix = "link"
): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  findLinks(text).forEach((link, index) => {
    if (link.start > last) {
      nodes.push(text.slice(last, link.start));
    }
    nodes.push(
      <a
        key={`${keyPrefix}-${index}`}
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 underline transition-colors"
      >
        {link.url}
      </a>
    );
    last = link.end;
  });
  if (last < text.length) {
    nodes.push(text.slice(last));
  }
  return nodes;
}
